// src/beta/components/Eyepatch.tsx
import type { CSSProperties, MouseEventHandler } from 'react';

interface EyepatchProps {
  src: string | null;
  alt: string;
  caption?: string;
  onClick?: MouseEventHandler;
  style?: CSSProperties;
}

/** Full-bleed photo container with a corner-anchored caption.
 *  Used by Focus hero, Scrubber, and the attract loop.
 *  When src is null, renders a charcoal fallback block. */
export function Eyepatch({ src, alt, caption, onClick, style }: EyepatchProps) {
  return (
    <div
      onClick={onClick}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        background: 'var(--vm-charcoal)',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            // iPad: disable callout/select on tap targets.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ...({ WebkitTouchCallout: 'none', WebkitUserSelect: 'none' } as any),
          }}
        />
      ) : (
        <div
          data-eyepatch-fallback
          aria-label={alt}
          role="img"
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--vm-stone)',
            fontFamily: 'var(--vm-font-heading)',
            fontSize: 11,
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
          }}
        >
          Image unavailable
        </div>
      )}

      {caption ? (
        <div
          data-eyepatch-caption
          style={{
            position: 'absolute',
            left: 'var(--vm-s-3)',
            bottom: 'var(--vm-s-3)',
            color: 'var(--vm-warm-white)',
            fontFamily: 'var(--vm-font-heading)',
            fontSize: 11,
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            textShadow: '0 1px 4px rgba(0,0,0,0.6)',
          }}
        >
          {caption}
        </div>
      ) : null}
    </div>
  );
}
